import React, { Component } from "react";
import EmpLaundryBenefitService from "../../services/emplaundrybenefitservice";

// Employee Laundry Benefit — per-month view of the ₹1,000/month personal-laundry
// allowance for the ~14 rostered employees: what was credited, what they used,
// what expired, and what they owe out-of-pocket. Plus a one-click "credit this
// month" action. Reads the read-only Store WebAPI proc; styles are scoped under
// .elb-root so they don't touch the rest of the portal.
const MONTHS_BACK = 12;

export default class EmpLaundryBenefit extends Component {
  constructor(props) {
    super(props);
    this.service = new EmpLaundryBenefitService();
    this.state = {
      month: this.currentMonth(),
      rows: [],
      orders: [],
      loading: true,
      error: "",
      refreshedAt: null,
      crediting: false,
      creditMsg: "",
      creditCls: "",
    };
  }

  componentDidMount() {
    this.load();
  }

  currentMonth = () => {
    const d = new Date();
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
  };

  monthOptions = () => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < MONTHS_BACK; i++) {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const val = y + "-" + String(m).padStart(2, "0");
      const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
      opts.push({ val, label });
      d.setMonth(d.getMonth() - 1);
    }
    return opts;
  };

  load = async () => {
    this.setState({ loading: true, error: "" });
    const s = await this.service.getReport(this.state.month);
    if (s && s.rows) {
      this.setState({
        rows: s.rows || [],
        orders: s.orders || [],
        loading: false,
        refreshedAt: new Date(),
      });
    } else {
      this.setState({
        rows: [],
        orders: [],
        loading: false,
        error: (s && s.message) || "Could not load the benefit report.",
      });
    }
  };

  onMonthChange = (e) => {
    this.setState({ month: e.target.value, creditMsg: "" }, this.load);
  };

  stamp = (d) =>
    d ? d.toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hour12: false }) : "";

  monthLabel = (val) => {
    const parts = (val || "").split("-");
    if (parts.length !== 2) return val || "";
    const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
    return dt.toLocaleString("en-GB", { month: "long", year: "numeric" });
  };

  inr = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");

  creditMonth = async () => {
    if (this.state.crediting) return;
    const ok = window.confirm(
      "Credit ₹1,000 to each of the 14 employees for " + this.monthLabel(this.state.month) + "?"
    );
    if (!ok) return;
    this.setState({ crediting: true, creditMsg: "", creditCls: "" });
    const r = await this.service.creditMonth();
    const result = r && r.actionResult ? r.actionResult : (r && r.message) || "Something went wrong.";
    if (result === "SUCCESS") {
      this.setState({
        crediting: false,
        creditCls: "ok",
        creditMsg: "Credited " + (r.creditedCount || 0) + " employee" + ((r.creditedCount || 0) === 1 ? "" : "s") + ", " + this.inr(r.creditedAmount) + ".",
      });
      this.load();
    } else if (result === "ALREADY_CREDITED") {
      this.setState({ crediting: false, creditCls: "warn", creditMsg: "Already credited this month." });
    } else if (result === "NO_ACTIVE_ROSTER") {
      this.setState({ crediting: false, creditCls: "bad", creditMsg: "No active employee roster to credit." });
    } else {
      this.setState({ crediting: false, creditCls: "bad", creditMsg: result });
    }
  };

  totals = () => {
    return (this.state.rows || []).reduce(
      (a, r) => {
        a.credited += Number(r.credited) || 0;
        a.used += Number(r.usedBenefit) || 0;
        a.expired += Number(r.expired) || 0;
        a.ordersTotal += Number(r.ordersTotal) || 0;
        a.outOfPocket += Number(r.outOfPocket) || 0;
        return a;
      },
      { credited: 0, used: 0, expired: 0, ordersTotal: 0, outOfPocket: 0 }
    );
  };

  downloadCsv = () => {
    const list = this.state.rows || [];
    if (!list.length) return;
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const header = [
      "Employee", "Mobile", "Customer ID", "Allowance", "Credited", "Used",
      "Remaining", "Expired", "Orders", "Order Total", "Payable (out-of-pocket)",
    ];
    const body = list.map((r) => [
      r.employeeName || "",
      r.mobileNo || "",
      r.customerID || "",
      Math.round(Number(r.monthlyAllowance) || 0),
      Math.round(Number(r.credited) || 0),
      Math.round(Number(r.usedBenefit) || 0),
      Math.round(Number(r.remaining) || 0),
      Math.round(Number(r.expired) || 0),
      Number(r.ordersCount) || 0,
      Math.round(Number(r.ordersTotal) || 0),
      Math.round(Number(r.outOfPocket) || 0),
    ]);
    const csv = [header, ...body].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee-laundry-benefit-" + this.state.month + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  renderRows() {
    if (this.state.loading) return <tr><td colSpan="10" className="elb-empty">Loading…</td></tr>;
    if (!this.state.rows.length) return <tr><td colSpan="10" className="elb-empty">No employees on the benefit roster for this month.</td></tr>;
    return this.state.rows.map((r, i) => {
      const payable = Number(r.outOfPocket) || 0;
      return (
        <tr key={r.customerID || i} className={payable > 0 ? "elb-owes" : ""}>
          <td><strong>{r.employeeName || "—"}</strong></td>
          <td className="elb-mono">{r.mobileNo || "—"}</td>
          <td className="num">{this.inr(r.monthlyAllowance)}</td>
          <td className="num">{this.inr(r.credited)}</td>
          <td className="num hi">{this.inr(r.usedBenefit)}</td>
          <td className="num dim">{this.inr(r.remaining)}</td>
          <td className={"num" + (Number(r.expired) > 0 ? " warn" : " dim")}>{this.inr(r.expired)}</td>
          <td className="num">{Number(r.ordersCount) || 0}</td>
          <td className="num">{this.inr(r.ordersTotal)}</td>
          <td className="num">
            {payable > 0
              ? <span className="elb-badge">{this.inr(payable)}</span>
              : <span className="elb-nil">{this.inr(0)}</span>}
          </td>
        </tr>
      );
    });
  }

  renderTotals() {
    if (this.state.loading || !this.state.rows.length) return null;
    const t = this.totals();
    return (
      <tr className="elb-total">
        <td colSpan="3">Totals</td>
        <td className="num">{this.inr(t.credited)}</td>
        <td className="num hi">{this.inr(t.used)}</td>
        <td className="num dim">—</td>
        <td className="num">{this.inr(t.expired)}</td>
        <td className="num">—</td>
        <td className="num">{this.inr(t.ordersTotal)}</td>
        <td className="num"><span className="elb-badge">{this.inr(t.outOfPocket)}</span></td>
      </tr>
    );
  }

  renderOrders() {
    const list = this.state.orders || [];
    return (
      <div className="elb-panel">
        <div className="elb-panelh">
          <h2>Orders this month</h2>
          <span>Every benefit-eligible order placed by a rostered employee</span>
        </div>
        <div className="elb-tw">
          <table>
            <thead>
              <tr>
                <th>Employee</th><th>Mobile</th>
                <th className="num">Order ID</th><th>Date</th>
                <th>Store</th><th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {this.state.loading ? (
                <tr><td colSpan="6" className="elb-empty">Loading…</td></tr>
              ) : !list.length ? (
                <tr><td colSpan="6" className="elb-empty">No orders for this month.</td></tr>
              ) : (
                list.map((o, i) => (
                  <tr key={i}>
                    <td>{o.employeeName || "—"}</td>
                    <td className="elb-mono">{o.mobileNo || "—"}</td>
                    <td className="num elb-mono">{"#" + o.orderID}</td>
                    <td className="dim">{o.orderDate || "—"}</td>
                    <td>{o.storeName || (o.storeID ? "Store " + o.storeID : "—")}</td>
                    <td className="num">{this.inr(o.finalOrderAmount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="elb-root">
        <style>{ELB_CSS}</style>
        <div className="elb-head">
          <div>
            <h1>Employee Laundry Benefit</h1>
            <p>&#8377;1,000/month personal-laundry allowance — usage and amounts payable.</p>
          </div>
          <div className="elb-actions">
            <button className="elb-credit" onClick={this.creditMonth} disabled={this.state.crediting}>
              {this.state.crediting ? "Crediting…" : "Credit this month"}
            </button>
          </div>
        </div>

        <div className="elb-controls">
          <div className="elb-ctl">
            <label>Month</label>
            <select value={this.state.month} onChange={this.onMonthChange} disabled={this.state.loading || this.state.crediting}>
              {this.monthOptions().map((o) => (
                <option key={o.val} value={o.val}>{o.label}</option>
              ))}
            </select>
          </div>
          <button className="elb-refresh" onClick={this.load} disabled={this.state.loading}>
            {this.state.loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className="elb-csv" onClick={this.downloadCsv} disabled={!this.state.rows.length}>Download CSV</button>
          <div className="elb-stamp">{this.state.refreshedAt ? "as at " + this.stamp(this.state.refreshedAt) : ""}</div>
        </div>

        {this.state.creditMsg ? (
          <div className={"elb-flash " + (this.state.creditCls || "")}>{this.state.creditMsg}</div>
        ) : null}
        {this.state.error ? <div className="elb-err">{this.state.error}</div> : null}

        <div className="elb-panel">
          <div className="elb-panelh">
            <h2>Benefit usage · {this.monthLabel(this.state.month)}</h2>
            <span>Used = subtotal drawn against &#8377;1,000 &middot; Payable = subtotal beyond &#8377;1,000</span>
          </div>
          <div className="elb-tw">
            <table>
              <thead>
                <tr>
                  <th>Employee</th><th>Mobile</th>
                  <th className="num">Allowance</th><th className="num">Credited</th>
                  <th className="num">Used</th><th className="num">Remaining</th>
                  <th className="num">Expired</th><th className="num">Orders</th>
                  <th className="num">Order Total</th><th className="num">Payable</th>
                </tr>
              </thead>
              <tbody>{this.renderRows()}</tbody>
              <tfoot>{this.renderTotals()}</tfoot>
            </table>
          </div>
        </div>

        {this.renderOrders()}
      </div>
    );
  }
}

const ELB_CSS = `
.elb-root{--elb-card:#fff;--elb-ink:#231a2b;--elb-ink2:#4f4557;--elb-muted:#7a6f83;--elb-faint:#a89fb0;--elb-line:#e8e2ee;--elb-line2:#f0ecf4;--elb-brand:#c71c78;--elb-good:#1f9d5b;--elb-warn:#c07d00;--elb-warnsoft:#fbf0d9;--elb-crit:#c0392b;--elb-critsoft:#fbe7e4;--elb-goodsoft:#e3f5ec;--elb-teal:#0f766e;font-variant-numeric:tabular-nums;color:var(--elb-ink);padding:4px 2px 40px}
.elb-root h1,.elb-root h2{margin:0;font-weight:700}
.elb-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;flex-wrap:wrap;margin-bottom:16px}
.elb-head h1{font-size:19px;letter-spacing:-.01em}
.elb-head p{margin:5px 0 0;font-size:12.5px;color:var(--elb-muted);max-width:640px;line-height:1.5}
.elb-actions{text-align:right}
.elb-credit{border:1px solid var(--elb-brand);background:var(--elb-brand);color:#fff;font-size:12px;font-weight:700;padding:8px 18px;border-radius:6px;cursor:pointer}
.elb-credit:hover:enabled{background:#a81563;border-color:#a81563}
.elb-credit:disabled{opacity:.55;cursor:default}
.elb-controls{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px}
.elb-ctl{display:flex;align-items:center;gap:8px}
.elb-ctl label{font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--elb-muted)}
.elb-ctl select{border:1px solid var(--elb-line);background:#fff;color:var(--elb-ink);font-size:12.5px;font-weight:600;padding:7px 10px;border-radius:6px;cursor:pointer}
.elb-refresh,.elb-csv{border:1px solid var(--elb-line);background:#fff;color:var(--elb-ink);font-size:12px;font-weight:600;padding:7px 16px;border-radius:6px;cursor:pointer}
.elb-refresh:hover:enabled,.elb-csv:hover:enabled{border-color:var(--elb-brand);color:var(--elb-brand)}
.elb-csv{border-color:var(--elb-brand);color:var(--elb-brand)}
.elb-csv:hover:enabled{background:var(--elb-brand);color:#fff}
.elb-refresh:disabled,.elb-csv:disabled{opacity:.55;cursor:default}
.elb-stamp{font-size:10.5px;color:var(--elb-faint);letter-spacing:.02em;margin-left:auto}
.elb-flash{border-radius:6px;padding:10px 14px;font-size:12.5px;margin-bottom:14px;font-weight:600}
.elb-flash.ok{background:var(--elb-goodsoft);color:var(--elb-good)}
.elb-flash.warn{background:var(--elb-warnsoft);color:var(--elb-warn)}
.elb-flash.bad{background:var(--elb-critsoft);color:var(--elb-crit)}
.elb-err{background:var(--elb-critsoft);color:var(--elb-crit);border-radius:6px;padding:10px 14px;font-size:12.5px;margin-bottom:16px}
.elb-panel{background:var(--elb-card);border:1px solid var(--elb-line);border-radius:8px;overflow:hidden;margin-bottom:18px}
.elb-panelh{display:flex;justify-content:space-between;align-items:baseline;gap:14px;padding:14px 16px;border-bottom:1px solid var(--elb-line)}
.elb-panelh h2{font-size:13.5px}
.elb-panelh span{font-size:11px;color:var(--elb-faint)}
.elb-tw{overflow-x:auto}
.elb-root table{width:100%;border-collapse:collapse;min-width:820px}
.elb-root thead th{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--elb-muted);text-align:left;padding:12px 14px;border-bottom:1px solid var(--elb-line);background:#faf8fc;white-space:nowrap}
.elb-root tbody td{padding:12px 14px;border-bottom:1px solid var(--elb-line2);vertical-align:middle;font-size:13px}
.elb-root tbody tr:hover{background:#faf8fc}
.elb-root th.num,.elb-root td.num{text-align:right}
.elb-root td.dim{color:var(--elb-faint)}
.elb-root td.hi{color:var(--elb-teal);font-weight:700}
.elb-root td.warn{color:var(--elb-warn);font-weight:700}
.elb-root tr.elb-owes{background:var(--elb-critsoft)}
.elb-root tr.elb-owes:hover{background:#f8dcd7}
.elb-badge{display:inline-block;font-size:12px;font-weight:700;color:var(--elb-crit);background:#fff;border:1px solid var(--elb-crit);padding:2px 9px;border-radius:10px;white-space:nowrap}
.elb-nil{color:var(--elb-faint)}
.elb-mono{font-variant-numeric:tabular-nums;white-space:nowrap}
.elb-empty{text-align:center;color:var(--elb-faint);padding:26px 0;font-size:12.5px}
.elb-root tfoot td{padding:12px 14px;font-size:12.5px;font-weight:700;color:var(--elb-ink);background:#faf8fc;border-top:2px solid var(--elb-line)}
.elb-root tfoot td.num{text-align:right}
.elb-root tfoot td.hi{color:var(--elb-teal)}
.elb-root tfoot td.dim{color:var(--elb-faint)}
`;
